import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Check, AlertTriangle, Camera, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import BuilderDiscountPopup from '@/components/BuilderDiscountPopup';
import CustomOrderModal from '@/components/CustomOrderModal';
import logger from '@/utils/logger';
import Seo from '@/components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MORSE_CODE = {
  'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.',
  'G': '--.', 'H': '....', 'I': '..', 'J': '.---', 'K': '-.-', 'L': '.-..',
  'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.',
  'S': '...', 'T': '-', 'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-',
  'Y': '-.--', 'Z': '--..', '0': '-----', '1': '.----', '2': '..---',
  '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...',
  '8': '---..', '9': '----.'
};

// Maker's spec: dot = 2mm, dash = 4mm, word separator bead = 2mm
const DOT_MM = 2;
const DASH_MM = 4;
const WORD_SEPARATOR_MM = 2;

// Bracelet sizes: 17 cm to 23 cm in 0.5 cm steps
const SIZE_OPTIONS_CM = Array.from({ length: 13 }, (_, i) => 17 + i * 0.5);
const DEFAULT_SIZE_CM = 19;

// Same source of truth as the product detail page + backend pricing module.
const CLASPS = [
  { id: 'lobster',          label: 'Lobster claw',              desc: 'Classic, secure, fully adjustable', upcharge: 0 },
  { id: 'magnetic-barrel',  label: 'Magnetic barrel',           desc: 'Discreet snap closure',            upcharge: 2 },
  { id: 'magnetic-heart',   label: 'Magnetic heart (standard)', desc: 'Snap-shut, perfect for gifts',     upcharge: 4 },
  { id: 'magnetic-heart-l', label: 'Magnetic heart (large)',    desc: 'Bolder heart accent',              upcharge: 6 },
  { id: 'magnetic-star',    label: 'Magnetic star (standard)',  desc: 'Whimsical star closure',           upcharge: 4 },
  { id: 'magnetic-star-l',  label: 'Magnetic star (large)',     desc: 'Statement star closure',           upcharge: 6 },
  { id: 'metal-drawstring', label: 'Metal drawstring',          desc: 'Sliding metal closure',            upcharge: 5 },
];
const basePriceForSize = (sizeCm) => (sizeCm <= 19 ? 24.95 : 26.95);
const claspUpcharge = (claspId) => (CLASPS.find((c) => c.id === claspId)?.upcharge || 0);
const calculatePrice = (sizeCm, claspId) => basePriceForSize(sizeCm) + claspUpcharge(claspId);

function calculateFit(text, sizeCm) {
  const maxLengthMm = Math.round(sizeCm * 10);
  const words = text.toUpperCase().split(/\s+/).filter(Boolean);
  let totalMm = 0;
  let dots = 0;
  let dashes = 0;
  for (const word of words) {
    for (const char of word) {
      const code = MORSE_CODE[char];
      if (!code) continue;
      for (const symbol of code) {
        if (symbol === '.') {
          dots += 1;
          totalMm += DOT_MM;
        } else if (symbol === '-') {
          dashes += 1;
          totalMm += DASH_MM;
        }
      }
    }
  }
  const separators = Math.max(0, words.length - 1);
  totalMm += separators * WORD_SEPARATOR_MM;
  const remainingMm = maxLengthMm - totalMm;
  const fits = totalMm <= maxLengthMm;
  // If it fits, pad the rest with 2 mm filler beads in a lighter colour
  const paddingBeads = fits ? Math.floor(remainingMm / DOT_MM) : 0;
  const finalLengthMm = fits ? totalMm + paddingBeads * DOT_MM : totalMm;
  const totalBeads = dots + dashes + separators + paddingBeads;
  return {
    total_mm: totalMm,
    remaining_mm: remainingMm,
    final_length_mm: finalLengthMm,
    max_length_mm: maxLengthMm,
    size_cm: sizeCm,
    fits,
    percent: Math.min(100, Math.round((totalMm / maxLengthMm) * 100)),
    dots,
    dashes,
    word_count: words.length,
    separators,
    padding_beads: paddingBeads,
    total_beads: totalBeads,
    has_text: totalMm > 0,
  };
}

export default function BraceletBuilderPage() {
  const [text, setText] = useState('');
  const [beadColor, setBeadColor] = useState('gold');
  const [sizeCm, setSizeCm] = useState(DEFAULT_SIZE_CM);
  const [clasp, setClasp] = useState('lobster');
  const [renderUrl, setRenderUrl] = useState(null);
  const [renderLoading, setRenderLoading] = useState(false);
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);
  const discountShownRef = useRef(false);

  const fit = useMemo(() => calculateFit(text, sizeCm), [text, sizeCm]);

  const handleImageLoaded = () => {
    // Show the discount popup once per visit, only after the customer has actually seen their rendered bracelet
    if (discountShownRef.current) return;
    if (typeof window !== 'undefined' && window.localStorage.getItem('ww-builder-discount-redeemed')) {
      return;
    }
    discountShownRef.current = true;
    // small delay so the photo can settle in
    setTimeout(() => setDiscountOpen(true), 700);
  };

  const handleGeneratePhoto = async () => {
    const cleaned = text.trim();
    if (!cleaned) {
      toast.error('Type a word or phrase first');
      return;
    }
    if (!fit.fits) {
      toast.error('Shorten your message or choose a larger size before generating a photo');
      return;
    }
    try {
      setRenderLoading(true);
      setRenderUrl(null);
      const { data } = await axios.post(`${API}/builder/render`, {
        text: cleaned,
        bead_color: beadColor,
        size_cm: sizeCm,
        clasp,
      }, { timeout: 90000 });
      if (data?.image_url) {
        setRenderUrl(`${process.env.REACT_APP_BACKEND_URL}${data.image_url}`);
      } else {
        toast.error("Couldn't generate the photo — please try again.");
      }
    } catch (err) {
      logger.error('Render failed', err);
      toast.error("Couldn't generate the photo. Please try again in a moment.");
    } finally {
      setRenderLoading(false);
    }
  };

  const colors = {
    gold: {
      bead: 'bg-gradient-to-b from-[#F4E7BD] via-[#D4AF37] to-[#B8972E]',
      shadow: 'shadow-[0_2px_4px_rgba(212,175,55,0.4)]',
      string: 'bg-[#8B7355]',
      name: 'Gold',
      accent: '#D4AF37'
    },
    silver: {
      bead: 'bg-gradient-to-b from-[#E8E8E8] via-[#C0C0C0] to-[#A8A8A8]',
      shadow: 'shadow-[0_2px_4px_rgba(192,192,192,0.4)]',
      string: 'bg-[#6B6B6B]',
      name: 'Silver',
      accent: '#C0C0C0'
    },
    roseGold: {
      bead: 'bg-gradient-to-b from-[#F5D0C5] via-[#E8A090] to-[#D4836B]',
      shadow: 'shadow-[0_2px_4px_rgba(232,160,144,0.4)]',
      string: 'bg-[#8B6B61]',
      name: 'Rose Gold',
      accent: '#E8A090'
    }
  };

  const currentColor = colors[beadColor];

  // Pre-compute Fit Calculator visual state once instead of repeating
  // nested ternaries throughout the JSX.
  const fitState = (() => {
    if (!fit.has_text) {
      return {
        kind: 'empty',
        cardBg: 'bg-[#F5F5F0]',
        iconWrap: 'bg-[#84A98C]/20',
        captionColor: 'text-[#84A98C]',
        bodyColor: 'text-[#84A98C]',
        title: 'Type a word to check',
      };
    }
    if (fit.fits) {
      return {
        kind: 'fits',
        cardBg: 'bg-[#354F52] text-[#FAF9F6]',
        iconWrap: 'bg-[#D4AF37]',
        captionColor: 'text-[#D4AF37]',
        bodyColor: 'text-[#CAD2C5]',
        title: `Fits! +${fit.padding_beads} cream filler beads to reach ${fit.size_cm.toFixed(1)} cm`,
      };
    }
    return {
      kind: 'too-long',
      cardBg: 'bg-[#BC4749]/10 border-2 border-[#BC4749] text-[#354F52]',
      iconWrap: 'bg-[#BC4749]',
      captionColor: 'text-[#BC4749]',
      bodyColor: 'text-[#52796F]',
      title: `Too long by ${Math.abs(fit.remaining_mm)} mm`,
    };
  })();

  // Generate beads from text
  const generateBeads = () => {
    const beads = [];
    const upperText = text.toUpperCase().slice(0, 60); // Limit to 60 chars
    
    for (let i = 0; i < upperText.length; i++) {
      const char = upperText[i];
      
      if (char === ' ') {
        // Spacer bead for word separation (larger, different color)
        beads.push({ type: 'word-spacer', key: `space-${i}` });
      } else if (MORSE_CODE[char]) {
        const morse = MORSE_CODE[char];
        for (let j = 0; j < morse.length; j++) {
          beads.push({
            type: morse[j] === '.' ? 'dot' : 'dash',
            key: `${char}-${i}-${j}`,
            delay: beads.length * 50
          });
        }
        // Letter separator bead (small colored dot between letters)
        if (i < upperText.length - 1 && upperText[i + 1] !== ' ') {
          beads.push({ type: 'letter-separator', key: `sep-${i}` });
        }
      }
    }
    
    return beads;
  };

  const beads = generateBeads();

  // Get morse code translation for display
  const getMorseTranslation = () => {
    return text.toUpperCase().split('').map(char => {
      if (char === ' ') return '/';
      return MORSE_CODE[char] || '';
    }).join(' ');
  };

  return (
    <div className="pt-28 pb-16 min-h-screen" data-testid="bracelet-builder-page">
      <Seo title="Build your own bracelet" description={"Design your own Morse code bracelet. Type any word, choose your wrist size and clasp, watch it come to life — then order. From €24.95."} path="/builder" />
      <div className="container-custom px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-caption text-[#84A98C] mb-4">Design Your Own</p>
          <h1 className="heading-display text-4xl md:text-5xl text-[#354F52] mb-4">
            Bracelet Builder
          </h1>
          <p className="text-[#52796F] max-w-lg mx-auto">
            Type your word or phrase and see it transform into a beautiful Morse code bracelet
          </p>
        </div>

        {/* Main Builder Section */}
        <div className="max-w-4xl mx-auto">
          {/* Input Section */}
          <div className="bg-[#F5F5F0] p-8 md:p-12 rounded-sm mb-8">
            <div className="text-center mb-8">
              <label className="text-caption text-[#84A98C] block mb-4">
                Enter Your Message
              </label>
              <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, 60))}
                placeholder="LOVE"
                className="input-underline max-w-md text-3xl"
                maxLength={60}
                data-testid="builder-text-input"
              />
              <p className="text-sm text-[#84A98C] mt-2">
                {text.length}/60 characters · spaces separate words
              </p>
            </div>

            {/* Color Selection */}
            <div className="flex justify-center gap-4 mb-8">
              {Object.entries(colors).map(([key, color]) => (
                <button
                  key={key}
                  onClick={() => setBeadColor(key)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-sm transition-all ${
                    beadColor === key 
                      ? 'bg-[#354F52] text-[#FAF9F6]' 
                      : 'bg-white text-[#354F52] hover:bg-[#354F52]/5'
                  }`}
                  data-testid={`color-${key}`}
                >
                  <div 
                    className={`w-8 h-8 rounded-full ${color.bead} ${color.shadow}`}
                  />
                  <span className="text-xs uppercase tracking-widest">{color.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Bracelet Preview */}
          <div className="bg-[#354F52] p-8 md:p-12 rounded-sm mb-8">
            <p className="text-caption text-[#84A98C] text-center mb-6">
              Your Bracelet Preview
            </p>

            {/* Bracelet Display */}
            <div className="relative py-8 px-4 min-h-[100px] flex items-center justify-center overflow-x-auto">
              {text ? (
                <div className="relative inline-flex items-center">
                  {/* String/cord */}
                  <div 
                    className={`absolute left-0 right-0 top-1/2 h-1 ${currentColor.string} -translate-y-1/2 rounded-full opacity-60`}
                    style={{ filter: 'blur(0.5px)' }}
                  />
                  
                  {/* Beads container - single line */}
                  <div className="relative flex items-center whitespace-nowrap">
                    {/* End cap left */}
                    <div className={`w-3 h-5 ${currentColor.bead} rounded-full ${currentColor.shadow} mr-2 flex-shrink-0`} />

                    {/* Left-side padding beads (lighter, fills bracelet to 19 cm) */}
                    {Array.from({ length: Math.floor(fit.padding_beads / 2) }).map((_, i) => (
                      <div
                        key={`pad-l-${i}`}
                        className="w-3 h-3 bg-[#E5DCC5] rounded-full mx-0.5 flex-shrink-0 opacity-90 shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
                        data-testid="preview-padding-bead"
                      />
                    ))}

                    {beads.map((bead, index) => {
                      if (bead.type === 'dot') {
                        return (
                          <div
                            key={bead.key}
                            className={`w-5 h-5 ${currentColor.bead} rounded-full mx-0.5 ${currentColor.shadow} bead-enter flex-shrink-0`}
                            style={{ animationDelay: `${bead.delay}ms` }}
                            data-testid="preview-dot"
                          />
                        );
                      }
                      if (bead.type === 'dash') {
                        return (
                          <div
                            key={bead.key}
                            className={`w-10 h-5 ${currentColor.bead} rounded-full mx-0.5 ${currentColor.shadow} bead-enter flex-shrink-0`}
                            style={{ animationDelay: `${bead.delay}ms` }}
                            data-testid="preview-dash"
                          />
                        );
                      }
                      if (bead.type === 'letter-separator') {
                        return (
                          <div
                            key={bead.key}
                            className="w-3 h-3 bg-[#84A98C] rounded-full mx-1.5 flex-shrink-0"
                            data-testid="preview-letter-separator"
                          />
                        );
                      }
                      if (bead.type === 'word-spacer') {
                        return (
                          <div
                            key={bead.key}
                            className="w-4 h-4 bg-[#52796F] rounded-full mx-2 flex-shrink-0"
                            data-testid="preview-word-spacer"
                          />
                        );
                      }
                      return null;
                    })}

                    {/* Right-side padding beads (lighter, fills bracelet to 19 cm) */}
                    {Array.from({ length: Math.ceil(fit.padding_beads / 2) }).map((_, i) => (
                      <div
                        key={`pad-r-${i}`}
                        className="w-3 h-3 bg-[#E5DCC5] rounded-full mx-0.5 flex-shrink-0 opacity-90 shadow-[0_1px_2px_rgba(0,0,0,0.15)]"
                        data-testid="preview-padding-bead"
                      />
                    ))}

                    {/* End cap right */}
                    <div className={`w-3 h-5 ${currentColor.bead} rounded-full ${currentColor.shadow} ml-2 flex-shrink-0`} />
                  </div>
                </div>
              ) : (
                <p className="text-[#84A98C] text-center">
                  Type something above to see your bracelet
                </p>
              )}
            </div>

            {/* Morse Code Translation */}
            {text && (
              <div className="text-center mt-6 pt-6 border-t border-[#52796F]/30">
                <p className="text-xs text-[#84A98C] uppercase tracking-widest mb-2">
                  Morse Code
                </p>
                <p className="text-[#CAD2C5] font-mono tracking-wider text-lg">
                  {getMorseTranslation()}
                </p>
              </div>
            )}
          </div>

          {/* AI Photo Preview */}
          <div className="bg-[#F5F5F0] p-6 md:p-8 rounded-sm mb-8" data-testid="ai-photo-block">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
              <div>
                <p className="text-caption text-[#84A98C] mb-1">Lifestyle photo preview</p>
                <p className="font-serif text-xl text-[#354F52] leading-tight">
                  See your bracelet on warm cream linen
                </p>
                <p className="text-xs text-[#84A98C] mt-1 max-w-md">
                  A vibe preview, not a bead-for-bead reproduction — your bracelet will be hand-strung to the exact pattern above.
                </p>
              </div>
              <Button
                type="button"
                onClick={handleGeneratePhoto}
                disabled={renderLoading || !fit.has_text || !fit.fits}
                className="btn-primary flex items-center gap-2 self-start sm:self-auto"
                data-testid="generate-ai-photo"
              >
                {renderLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    Rendering…
                  </>
                ) : (
                  <>
                    <Camera className="w-4 h-4" strokeWidth={1.5} />
                    {renderUrl ? 'Regenerate' : 'Generate AI Photo'}
                  </>
                )}
              </Button>
            </div>

            <div
              className="relative w-full aspect-[4/3] rounded-sm overflow-hidden bg-gradient-to-br from-[#F0E9D2] via-[#E8DEC2] to-[#D9C9A4] flex items-center justify-center"
              data-testid="ai-photo-canvas"
            >
              {renderLoading && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[#F5F5F0]/70 backdrop-blur-sm z-10">
                  <Loader2 className="w-8 h-8 text-[#354F52] animate-spin" strokeWidth={1.5} />
                  <p className="text-sm text-[#52796F]">Crafting your photo… this takes ~10 seconds</p>
                </div>
              )}
              {renderUrl ? (
                <img
                  src={renderUrl}
                  alt={`AI rendering of a ${beadColor} Morse code bracelet for the word "${text}"`}
                  className="w-full h-full object-cover"
                  data-testid="ai-photo-result"
                  onLoad={handleImageLoaded}
                />
              ) : (
                <div className="text-center px-6 py-10">
                  <Camera className="w-10 h-10 text-[#84A98C] mx-auto mb-3" strokeWidth={1.25} />
                  <p className="text-[#52796F] font-serif text-lg">
                    {fit.has_text
                      ? 'Click "Generate AI Photo" to see your bracelet on cream linen with warm Mediterranean light.'
                      : 'Type a word above, then click "Generate AI Photo".'}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Bracelet Size Selector */}
          <div className="bg-[#F5F5F0] p-6 rounded-sm mb-8" data-testid="size-selector">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
              <div>
                <p className="text-caption text-[#84A98C] mb-1">Choose your wrist size</p>
                <p className="font-serif text-xl text-[#354F52]">
                  Bracelet length: <span className="text-[#D4AF37]" data-testid="selected-size">{sizeCm.toFixed(1)} cm</span>
                </p>
              </div>
              <p className="text-xs text-[#84A98C] sm:text-right max-w-xs">
                Standard 19 cm fits most adult wrists. Choose smaller for slim wrists, larger for a relaxed fit.
                {' '}
                <Link
                  to="/sizing-guide"
                  className="underline decoration-[#D4AF37]/40 underline-offset-2 hover:text-[#D4AF37] transition-colors"
                  data-testid="builder-sizing-guide-link"
                >
                  Not sure? Measure your wrist →
                </Link>
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {SIZE_OPTIONS_CM.map((s) => {
                const active = Math.abs(s - sizeCm) < 0.001;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSizeCm(s)}
                    aria-pressed={active}
                    className={`px-3 py-2 rounded-full text-sm uppercase tracking-wider transition-colors ${
                      active
                        ? 'bg-[#354F52] text-[#FAF9F6]'
                        : 'bg-white text-[#354F52] hover:bg-[#354F52]/10'
                    } ${s === DEFAULT_SIZE_CM && !active ? 'ring-1 ring-[#D4AF37]/60' : ''}`}
                    data-testid={`size-${s.toFixed(1).replace('.', '-')}`}
                  >
                    {s.toFixed(1)} cm
                    {s === DEFAULT_SIZE_CM && !active && (
                      <span className="ml-1 text-[10px] text-[#D4AF37]">★</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Clasp selector */}
          <div className="bg-[#F5F5F0] p-6 rounded-sm mb-8" data-testid="clasp-selector">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
              <div>
                <p className="text-caption text-[#84A98C] mb-1">Choose your clasp</p>
                <p className="font-serif text-xl text-[#354F52]">
                  Clasp: <span className="text-[#D4AF37]" data-testid="selected-clasp">
                    {CLASPS.find((c) => c.id === clasp)?.label}
                  </span>
                </p>
              </div>
              <p className="text-xs text-[#84A98C] sm:text-right max-w-xs">
                Lobster is included. Magnetic and drawstring options add a small upcharge.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {CLASPS.map((c) => {
                const active = c.id === clasp;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setClasp(c.id)}
                    aria-pressed={active}
                    className={`flex items-start justify-between gap-3 px-4 py-3 rounded-sm text-left transition-colors border ${
                      active
                        ? 'bg-[#354F52] text-[#FAF9F6] border-[#354F52]'
                        : 'bg-white text-[#354F52] border-[#CAD2C5]/50 hover:border-[#D4AF37]/60'
                    }`}
                    data-testid={`clasp-${c.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium leading-tight">{c.label}</p>
                      <p className={`text-xs mt-1 ${active ? 'text-[#CAD2C5]' : 'text-[#84A98C]'}`}>
                        {c.desc}
                      </p>
                    </div>
                    <span
                      className={`text-xs font-medium shrink-0 ${
                        active ? 'text-[#D4AF37]' : c.upcharge ? 'text-[#D4AF37]' : 'text-[#84A98C]'
                      }`}
                    >
                      {c.upcharge ? `+€${c.upcharge.toFixed(2)}` : 'Included'}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Will it fit? — Live length calculator (next to the designer) */}
          <div
            className={`p-7 rounded-sm mb-8 transition-colors ${fitState.cardBg}`}
            data-testid="fit-calculator"
          >
            <div className="flex items-start gap-4 mb-4">
              <div
                className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${fitState.iconWrap}`}
              >
                {fitState.kind === 'empty' && (
                  <Sparkles className="w-5 h-5 text-[#84A98C]" strokeWidth={1.5} />
                )}
                {fitState.kind === 'fits' && (
                  <Check className="w-5 h-5 text-[#354F52]" strokeWidth={2.5} />
                )}
                {fitState.kind === 'too-long' && (
                  <AlertTriangle className="w-5 h-5 text-[#FAF9F6]" strokeWidth={2} />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-caption mb-1 ${fitState.captionColor}`}>
                  Will it fit?
                </p>
                <p className="font-serif text-xl md:text-2xl leading-tight mb-1">
                  {fitState.title}
                </p>
                <p
                  className={`text-xs ${fitState.bodyColor}`}
                  data-testid="fit-summary"
                >
                  Bracelet length: {fit.size_cm.toFixed(1)} cm ({fit.max_length_mm} mm) · dots 2 mm · dashes 4 mm · word
                  separator bead 2 mm
                </p>
              </div>
            </div>

            {fit.has_text && (
              <>
                {/* Progress bar */}
                <div
                  className={`h-2.5 rounded-full overflow-hidden mb-2 ${
                    fit.fits ? 'bg-[#52796F]' : 'bg-[#FAF9F6]'
                  }`}
                >
                  <div
                    className={`h-full transition-all ${
                      fit.fits ? 'bg-[#D4AF37]' : 'bg-[#BC4749]'
                    }`}
                    style={{ width: `${fit.percent}%` }}
                    data-testid="fit-progress"
                  />
                </div>
                <div
                  className={`flex justify-between text-xs mb-4 ${
                    fit.fits ? 'text-[#CAD2C5]' : 'text-[#84A98C]'
                  }`}
                >
                  <span>0</span>
                  <span>
                    <strong>{fit.total_mm} mm</strong> used ({(fit.total_mm / 10).toFixed(1)} cm)
                  </span>
                  <span>{fit.max_length_mm} mm</span>
                </div>

                {/* Stats */}
                <div
                  className={`grid grid-cols-2 sm:grid-cols-5 gap-3 text-center text-xs ${
                    fit.fits ? 'text-[#CAD2C5]' : 'text-[#52796F]'
                  }`}
                  data-testid="fit-stats"
                >
                  <div>
                    <p className={`font-serif text-xl ${fit.fits ? 'text-[#FAF9F6]' : 'text-[#354F52]'}`}>
                      {fit.dots}
                    </p>
                    <p>Dots</p>
                  </div>
                  <div>
                    <p className={`font-serif text-xl ${fit.fits ? 'text-[#FAF9F6]' : 'text-[#354F52]'}`}>
                      {fit.dashes}
                    </p>
                    <p>Dashes</p>
                  </div>
                  <div>
                    <p className={`font-serif text-xl ${fit.fits ? 'text-[#FAF9F6]' : 'text-[#354F52]'}`}>
                      {fit.separators}
                    </p>
                    <p>Spacer{fit.separators === 1 ? '' : 's'}</p>
                  </div>
                  <div data-testid="stat-padding">
                    <p className="font-serif text-xl text-[#E5DCC5]">
                      {fit.padding_beads}
                    </p>
                    <p>Filler beads</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1 border-t sm:border-t-0 sm:border-l border-[#52796F] pt-2 sm:pt-0" data-testid="stat-total">
                    <p className={`font-serif text-2xl ${fit.fits ? 'text-[#D4AF37]' : 'text-[#BC4749]'}`}>
                      {fit.total_beads}
                    </p>
                    <p>Total beads</p>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Legend */}
          <div className="bg-[#F5F5F0] p-6 rounded-sm mb-8">
            <p className="text-caption text-[#84A98C] mb-4 text-center">How to Read</p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 ${currentColor.bead} rounded-full ${currentColor.shadow}`} />
                <span className="text-[#354F52] text-sm">Dot (short)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-5 ${currentColor.bead} rounded-full ${currentColor.shadow}`} />
                <span className="text-[#354F52] text-sm">Dash (long)</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-[#84A98C] rounded-full" />
                <span className="text-[#354F52] text-sm">Letter separator</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-4 h-4 bg-[#52796F] rounded-full" />
                <span className="text-[#354F52] text-sm">Word space</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-[#E5DCC5] rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.15)]" />
                <span className="text-[#354F52] text-sm">Filler bead (pads to {fit.size_cm.toFixed(1)} cm)</span>
              </div>
            </div>
          </div>

          {/* Price Estimate */}
          {text && (
            <div className="bg-white p-6 rounded-sm border border-[#CAD2C5]/50 mb-8">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                  <p className="text-[#84A98C] text-sm">Estimated Price</p>
                  <p className="text-3xl font-serif text-[#D4AF37]" data-testid="builder-price">
                    €{calculatePrice(sizeCm, clasp).toFixed(2)}
                  </p>
                  <p className="text-xs text-[#84A98C]">
                    {sizeCm <= 19 ? 'Base €24.95' : 'Base €26.95'}
                    {claspUpcharge(clasp) > 0 && (
                      <> · +€{claspUpcharge(clasp).toFixed(2)} for {CLASPS.find((c) => c.id === clasp)?.label}</>
                    )}
                  </p>
                </div>
                {fit.fits ? (
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setCustomModalOpen(true)}
                      className="text-sm text-[#52796F] hover:text-[#354F52] underline underline-offset-4 whitespace-nowrap"
                      data-testid="open-custom-order-modal"
                    >
                      Prefer to chat first?
                    </button>
                    <Link
                      to={`/custom?text=${encodeURIComponent(text)}&style=${beadColor}&size=${sizeCm}&clasp=${clasp}&totalBeads=${fit.total_beads}&fillerBeads=${fit.padding_beads}&dots=${fit.dots}&dashes=${fit.dashes}`}
                      className="btn-primary flex items-center gap-2"
                      data-testid="order-this-bracelet"
                    >
                      <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                      Order This Bracelet
                      <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                    </Link>
                  </div>
                ) : (
                  <div className="text-right" data-testid="order-blocked">
                    <p className="text-sm text-[#BC4749] font-medium mb-1">
                      Won&apos;t fit on a {fit.size_cm.toFixed(1)} cm bracelet
                    </p>
                    <p className="text-xs text-[#84A98C]">
                      Shorten your message or pick a longer size above
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Popular Examples */}
          <div className="text-center">
            <p className="text-caption text-[#84A98C] mb-4">Try These Popular Messages</p>
            <div className="flex flex-wrap justify-center gap-3">
              {['LOVE', 'HOPE', 'JOY', 'MOM', 'FAITH', 'DREAM', 'BRAVE'].map((word) => (
                <button
                  key={word}
                  onClick={() => setText(word)}
                  className="px-4 py-2 bg-[#F5F5F0] hover:bg-[#354F52] hover:text-[#FAF9F6] text-[#354F52] rounded-full text-sm uppercase tracking-wider transition-colors"
                  data-testid={`try-${word.toLowerCase()}`}
                >
                  {word}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <BuilderDiscountPopup open={discountOpen} onClose={() => setDiscountOpen(false)} />
      <CustomOrderModal
        open={customModalOpen}
        onOpenChange={setCustomModalOpen}
        customText={text}
        sizeCm={sizeCm}
        clasp={clasp}
        unitPrice={calculatePrice(sizeCm, clasp)}
        builderStats={{
          sizeCm,
          totalBeads: fit?.total_beads || 0,
          dots: fit?.dots || 0,
          dashes: fit?.dashes || 0,
          fillerBeads: fit?.padding_beads || 0,
        }}
      />
    </div>
  );
}
